import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { getGameRecord } from '@sportninja/common/services/game/attendanceServices'
import { toVenueLocalDate } from '@sportninja/common/ts-utils/date-utils'

interface GameScheduleRecord {
  starts_at?: string
  sport_id?: string | number | null
  venue?: { timezone?: string | null } | null
}

/**
 * What the Player Finder needs off the game: its date and sport. Reads the same
 * `gameRaw.byId` cache the Attendance modal already populates via
 * useAttendanceGameMeta — so this resolves from cache rather than issuing a
 * second request.
 *
 * `date` is the game's VENUE-LOCAL date, YYYY-MM-DD (SN-8309): the candidates
 * endpoint matches it against each listing's available weekdays, and a
 * late-evening game is already the next day in UTC.
 *
 * `sportId` comes off the same record (no extra request) and scopes the
 * position filter/column and the candidate's season stats.
 *
 * `getGameRecord` unwraps the `{ data }` envelope internally, so `query.data`
 * is the game record directly.
 */
export const useAttendanceGameSchedule = (gameId?: string) => {
  const query = useQuery({
    queryKey: queryKeys.gameRaw.byId(gameId),
    queryFn: () =>
      getGameRecord(gameId as string) as Promise<GameScheduleRecord>,
    enabled: Boolean(gameId),
  })

  const game = query.data
  return {
    date: toVenueLocalDate(game?.starts_at, game?.venue?.timezone),
    sportId: game?.sport_id != null ? String(game.sport_id) : undefined,
    loading: Boolean(gameId) && query.isLoading,
  }
}
